import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ShieldAlert, X } from "lucide-react";
import { subscribeAccessDenied } from "../utils/accessDeniedBus";
import { requestBusinessSelection } from "../utils/businessSelectionBus";

function isBusinessContextAccess(payload) {
  const reason = String(payload?.reason || "").toLowerCase();
  const text = String(
    [
      payload?.message,
      payload?.subtitle,
      payload?.title,
      payload?.code,
      payload?.errorCode,
    ]
      .filter(Boolean)
      .join(" "),
  ).toLowerCase();

  return (
    reason === "business-context" ||
    text.includes("business context required") ||
    text.includes("business context missing") ||
    text.includes("missing business context") ||
    text.includes("please select a business") ||
    text.includes("select a business first") ||
    text.includes("businessid is required") ||
    text.includes("business id is required") ||
    text.includes("businessid missing in token") ||
    text.includes("missing businessid in token") ||
    text.includes("missing businessid in user claims") ||
    text.includes("businessid in user claims") ||
    text.includes("missing businessid") ||
    text.includes("invalid or missing businessid in token")
  );
}

export default function AccessDeniedModal() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    return subscribeAccessDenied(payload => {
      if (isBusinessContextAccess(payload)) {
        requestBusinessSelection({
          ...payload,
          reason: "business-context",
          open: false,
          source: window.location.pathname,
        });
        setOpen(false);
        setDetail(null);
        return;
      }

      setDetail(payload || null);
      setOpen(true);
    });
  }, []);

  if (!open) return null;

  const reason = String(detail?.reason || "").toLowerCase();
  const isBusinessContext = reason === "business-context";

  const title =
    detail?.title ||
    (isBusinessContext ? "Select a business" : "You don't have permission");
  const subtitle =
    detail?.subtitle ??
    (isBusinessContext
      ? "Choose a business from the topbar to continue in business-scoped modules."
      : "Your account is logged in, but this page or action is not available for your current access.");
  const message =
    detail?.message ||
    (isBusinessContext
      ? "Business context is required before this page can load."
      : "Access was denied for this page or action.");
  const badgeLabel =
    detail?.badgeLabel ||
    (isBusinessContext ? "Business required" : "Access denied");
  const showNoAccessAction =
    !isBusinessContext && !detail?.hideNoAccessAction;

  const handleClose = () => {
    setOpen(false);
    setDetail(null);
  };

  const handleOpenNoAccess = () => {
    handleClose();
    nav("/no-access");
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-md rounded-[10px] border border-emerald-100 bg-white p-6 shadow-2xl">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          aria-label="Close access denied dialog"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-xl bg-amber-50 p-3">
            {isBusinessContext ? (
              <AlertTriangle className="h-7 w-7 text-amber-700" />
            ) : (
              <ShieldAlert className="h-7 w-7 text-amber-700" />
            )}
          </div>

          <div className="flex-1">
            <div className="mb-3 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                {badgeLabel}
              </span>
            </div>

            <h2 className="mb-1 text-lg font-bold text-slate-900">{title}</h2>
            {subtitle ? (
              <p className="mb-2 text-sm text-slate-600">{subtitle}</p>
            ) : null}
            <p className="mb-6 text-sm text-slate-500">{message}</p>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              {showNoAccessAction && (
                <button
                  type="button"
                  onClick={handleOpenNoAccess}
                  className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Open access page
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
